import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";
import { useSessionTimeout } from "./hooks/useSessionTimeout";
import { InstallPrompt } from "./components/InstallPrompt";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { PushNotificationsManager } from "./components/PushNotificationsManager";
import { OfflineBanner } from "./components/OfflineBanner";
import * as Sentry from "@sentry/react";
import Welcome from "./pages/Welcome";
import Signup from "./pages/Signup";
import CompanySignup from "./pages/CompanySignup";
import Onboarding from "./pages/Onboarding";
import Home from "./pages/Home";
import History from "./pages/History";
import Supervisor from "./pages/Supervisor";
import SupervisorHub from "./pages/SupervisorHub";
import PreShiftCheckin from "./pages/PreShiftCheckin";
import Corroborate from "./pages/Corroborate";
import Leaderboard from "./pages/Leaderboard";
import Profile from "./pages/Profile";
// ReportedHistory removed - Just Culture compliance
import UserDetail from "./pages/UserDetail";
import AdminDashboard from "./pages/admin/AdminDashboard";
import SuperAdminDashboard from "./pages/admin/SuperAdminDashboard";
import SiteAdminDashboard from "./pages/admin/SiteAdminDashboard";
import AdminLogin from "./pages/AdminLogin";
import Quests from "./pages/Quests";
import QuestDetail from "./pages/QuestDetail";
import ReportDetail from "./pages/ReportDetail";
import ReportIncident from "./pages/ReportIncident";
import IncidentDetail from "./pages/IncidentDetail";
import MicroLearning from "./pages/MicroLearning";
import QuickIncident from "./pages/QuickIncident";
import LearningModules from "./pages/LearningModules";
import CreateLearning from "./pages/admin/CreateLearning";
import CreateIntervention from "./pages/admin/CreateIntervention";
import AdminInspectionData from "./pages/admin/AdminInspectionData";
import ClusterDiagnostics from "./pages/admin/ClusterDiagnostics";
import AdminNetworkMetrics from "./pages/admin/AdminNetworkMetrics";
import AdminFatigueManagement from "./pages/admin/AdminFatigueManagement";
import NotFound from "./pages/NotFound";
import HelpCenter from "./pages/HelpCenter";
import HelpCategory from "./pages/HelpCategory";
import HelpArticle from "./pages/HelpArticle";
import PrivacyPolicy from "./pages/PrivacyPolicy";

const queryClient = new QueryClient();

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const AppRoutes = () => {
  useSessionTimeout();

  return (
    <SentryRoutes>
      <Route path="/" element={<Welcome />} />
      <Route path="/signup" element={<Welcome />} />
      <Route path="/company-signup" element={<CompanySignup />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/home" element={<Home />} />
      <Route path="/history" element={<History />} />
      <Route path="/supervisor" element={<SupervisorHub />} />
      <Route path="/supervisor-legacy" element={<Supervisor />} />
      <Route path="/pre-shift-checkin" element={<PreShiftCheckin />} />
      <Route path="/corroborate/:reportId" element={<Corroborate />} />
      {/* Reported History route removed - Just Culture compliance */}
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/quests" element={<Quests />} />
      <Route path="/quests/:id" element={<QuestDetail />} />
      <Route path="/report/:id" element={<ReportDetail />} />
      <Route path="/report-incident" element={<ReportIncident />} />
      <Route path="/quick-incident" element={<QuickIncident />} />
      <Route path="/incident/:id" element={<IncidentDetail />} />
      <Route path="/learning-modules" element={<LearningModules />} />
      <Route path="/learning/:id" element={<MicroLearning />} />
      <Route path="/user/:workerId" element={<UserDetail />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/create-learning" element={<CreateLearning />} />
      <Route path="/admin/create-intervention" element={<CreateIntervention />} />
      <Route path="/admin/inspection-data" element={<AdminInspectionData />} />
      <Route path="/admin/cluster-diagnostics" element={<ClusterDiagnostics />} />
      <Route path="/admin/network-metrics" element={<AdminNetworkMetrics />} />
      <Route path="/admin/fatigue" element={<AdminFatigueManagement />} />
      <Route path="/site-admin" element={<SiteAdminDashboard />} />
      <Route path="/platform-admin" element={<SuperAdminDashboard />} />
      <Route path="/help" element={<HelpCenter />} />
      <Route path="/help/category/:categoryId" element={<HelpCategory />} />
      <Route path="/help/:slug" element={<HelpArticle />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="*" element={<NotFound />} />
      <Route path="*" element={<NotFound />} />
    </SentryRoutes>
  );
};

const App = () => (
  <Sentry.ErrorBoundary fallback={<ErrorBoundary><div /></ErrorBoundary>} showDialog>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <InstallPrompt />
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <OfflineBanner />
            <PushNotificationsManager />
            <AppRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </LanguageProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
